import React, { useState } from 'react';
import { Button } from "@mui/material";
import InitiatePayoutDialog from "../InitiatePayoutDialog";
import Busy from "../Busy";
import { requestInitiatePayouts, requestReadyForPayoutTransactionsForAccount } from "../../views/requests/account-report";
import { getErrorMessage } from "../../util/NetworkErrorUtil";
import { useSnackbar } from "../../context/snackbar-context";
import { Location } from "../../types/Location";

interface PayoutTransaction {
  bookingTransactionId: string;
}

interface PayoutTransactionsResponse {
  content: PayoutTransaction[];
}

interface InitiatePayoutsProps {
  accountId: string;
  locations: Location[];
}

/**
 * Component for initiating payouts for a supplier account.
 * It displays a button that opens a dialog to select a location and initiate payouts for the selected location.
 * @param param0
 * @param param0.accountId The ID of the supplier account.
 * @param param0.locations The list of locations associated with the supplier account.
 */
const InitiatePayouts = ({ accountId, locations }: InitiatePayoutsProps) => {
  const [showDialog, setShowDialog] = useState(false);

  const { snackbarShowMessage } = useSnackbar();

  const openDialog = (): void => {
    setShowDialog(true);
  };

  const handleInitiatePayouts = async (locationId: string): Promise<void> => {
    Busy.set(true);
    try {
      const payoutTransactions = await requestReadyForPayoutTransactionsForAccount({
        locationId: locationId === 'All' ? '' : locationId,
        sortBy: 'INVOICE_NUMBER',
        sortDir: 'asc'
      }).then((response) => response.body as PayoutTransactionsResponse);

      const transactionIds = payoutTransactions.content.map(
        (payoutTransaction) => payoutTransaction.bookingTransactionId
      );

      await requestInitiatePayouts({
        supplierAccountId: accountId,
        transactionIds: transactionIds
      }).then(() => {
        snackbarShowMessage('Payouts initiated successfully.', 'success');
      });
    } catch (error) {
      snackbarShowMessage(
        getErrorMessage(
          error,
          'Failed to initiate payouts. Please try again or contact SecurSpace if this issue persists.'
        ),
        'error'
      );
    } finally {
      Busy.set(false);
      setShowDialog(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={openDialog}
      >
        Initiate Available Payouts
      </Button>
      <InitiatePayoutDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onConfirm={handleInitiatePayouts}
        locations={locations}
      />
    </>
  );
};

export default InitiatePayouts;
